/** Step 1 — which order we are asking the court for, which file(s) it relates to, and "Fill with AI" (reads the ticked files + the whole file). */
import { useMemo, useState } from "react";
import { FileText, Loader2, Sparkles, Lock, ExternalLink, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { getLegalCaseFilePreviewUrl } from "@/lib/api";
import { FilePreviewModal } from "@/components/FilePreviewModal";
import { TONE, fmtWhen } from "./n244Shared";

export function FilePicker({ files, picked, setPicked, testId = "n244-file", max = 12, hint }) {
  const [filter, setFilter] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  // Dean, 23 Oct 2026: eye = in-window preview, never a forced download (OneDrive via its embed page, the rest via the proxy)
  const openPreview = async (e, f) => {
    e.preventDefault(); e.stopPropagation();
    if (f.origin === "OneDrive" && f.file_id) {
      setPreviewing(f.key);
      try {
        const r = await getLegalCaseFilePreviewUrl(f.file_id);
        setPreview({ name: f.name, source: "OneDrive", url: f.url, preview_src: r.url, preview_kind: "embedded" });
      } catch (err) {
        toast.error("Couldn't open preview: " + (err.response?.data?.detail || err.message));
      } finally { setPreviewing(null); }
    } else {
      setPreview({ name: f.name, source: f.origin, url: f.url, proxy_url: f.proxy_url });
    }
  };
  const rows = useMemo(() => files.filter((f) => !filter || (f.name || "").toLowerCase().includes(filter.toLowerCase())), [files, filter]);
  const groups = useMemo(() => [...new Set(rows.map((f) => f.category_label))].map((g) => ({ g, rows: rows.filter((f) => f.category_label === g) })), [rows]);
  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : p.length >= max ? p : [...p, k]));
  return (
    <div className="space-y-1.5" data-testid={`${testId}-picker`}>
      <div className="flex items-center gap-2">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter files…" className="h-7 text-xs" data-testid={`${testId}-filter`} />
        <span className="text-[10px] text-slate-500 shrink-0">{picked.length} ticked{hint ? ` · ${hint}` : ""}</span>
      </div>
      <div className="border rounded divide-y max-h-56 overflow-y-auto bg-white">
        {!rows.length && <div className="p-3 text-[11px] text-slate-500">No documents on this file yet.</div>}
        {groups.map(({ g, rows: rs }) => (
          <div key={g} className="p-2">
            <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1">{g}</div>
            {rs.map((f) => (
              <label key={f.key} className="group flex items-start gap-2 py-0.5 text-[11px] cursor-pointer" data-testid={`${testId}-${f.key.replace(/[^a-zA-Z0-9]/g, "_")}`} title={f.name}>
                <Checkbox checked={picked.includes(f.key)} onCheckedChange={() => toggle(f.key)} className="mt-0.5" />
                <span className="min-w-0 flex-1 truncate">{f.name}</span>
                <button type="button" onClick={(e) => openPreview(e, f)} disabled={previewing === f.key} title="Preview in window"
                  className="shrink-0 text-indigo-600 opacity-60 hover:opacity-100 disabled:opacity-40" data-testid={`${testId}-preview-${f.key.replace(/[^a-zA-Z0-9]/g, "_")}`}>
                  {previewing === f.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                <span className="text-slate-400 shrink-0">{f.origin}{f.last_modified ? ` · ${String(f.last_modified).slice(0, 10)}` : ""}</span>
              </label>
            ))}
          </div>
        ))}
      </div>
      <FilePreviewModal file={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

function Previous({ apps }) {
  if (!apps?.length) return null;
  return (
    <div className="border rounded divide-y" data-testid="n244-previous">
      <div className="px-2 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide">N244s already on this file</div>
      {apps.map((a) => (
        <div key={a.id} className="flex items-center gap-2 px-2 py-1.5 text-[11px]" data-testid={`n244-previous-${a.id}`}>
          {a.status === "sent" ? <Lock className="w-3 h-3 text-emerald-600 shrink-0" /> : <FileText className="w-3 h-3 text-slate-400 shrink-0" />}
          <span className="min-w-0 flex-1 truncate">{a.order_label} · {a.court_name}{a.claim_number ? ` · ${a.claim_number}` : ""} · <span className="text-slate-500">{a.status} {fmtWhen(a.created_at)} by {a.created_by}</span></span>
          {a.files?.[0]?.web_url && <a href={a.files[0].web_url} target="_blank" rel="noreferrer" className="text-blue-700 inline-flex items-center gap-1 shrink-0"><ExternalLink className="w-3 h-3" />bundle</a>}
        </div>
      ))}
    </div>
  );
}

export function N244OrderStep({ ctx, form, setForm, onAi, aiBusy, aiStage, aiResult }) {
  const [instructions, setInstructions] = useState("");
  const ot = ctx.order_types.find((o) => o.key === form.order_type);
  const setPicked = (fn) => setForm((f) => ({ ...f, related_keys: typeof fn === "function" ? fn(f.related_keys) : fn }));
  return (
    <div className="space-y-3">
      <Previous apps={ctx.applications} />
      {ctx.warnings?.map((w, i) => <div key={i} className="rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900" data-testid={`n244-warning-${i}`}>⚠ {w}</div>)}
      <div>
        <div className="text-[11px] font-medium text-slate-600 mb-1">What order are we asking the court to make?</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="n244-order-types">
          {ctx.order_types.map((o) => (
            <button key={o.key} type="button" disabled={aiBusy} onClick={() => setForm((f) => ({ ...f, order_type: o.key, n244: { ...f.n244, hearing_mode: o.hearing_mode }, draft_order: { ...f.draft_order, include: o.draft_order } }))}
              data-testid={`n244-type-${o.key}`} aria-pressed={form.order_type === o.key}
              className={cn("text-left border rounded-md p-2.5 transition-colors", form.order_type === o.key ? TONE[o.tone] : "border-slate-200 hover:bg-slate-50", aiBusy && "opacity-60")}>
              <div className="text-xs font-semibold">{o.label}</div>
              <div className="text-[10px] text-slate-600 mt-0.5 leading-snug">{o.blurb}</div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[11px] font-medium text-slate-600 mb-1">Which document(s) does this application relate to? <span className="font-normal text-slate-500">— e.g. the order that just came in, the process server's certificate, the notice of hearing</span></div>
        <FilePicker files={ctx.files} picked={form.related_keys} setPicked={setPicked} hint="read by the AI and offered as enclosures" />
      </div>
      <div className="border rounded-md p-3 bg-amber-50/60 border-amber-200 space-y-2" data-testid="n244-ai-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800"><Sparkles className="w-4 h-4 text-amber-600 shrink-0" />Fill with AI</div>
          <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700 shrink-0" disabled={aiBusy || !form.related_keys.length} title={form.related_keys.length ? undefined : "Tick the document(s) the application relates to first"} onClick={() => onAi(instructions)} data-testid="n244-ai-fill">
            {aiBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}{aiBusy ? "Working…" : form.related_keys.length ? `Fill with AI from ${form.related_keys.length} file${form.related_keys.length === 1 ? "" : "s"}` : "Fill with AI — tick a document first"}
          </Button>
        </div>
        {aiBusy && <div className="text-[11px] text-amber-900 flex items-center gap-1.5" data-testid="n244-ai-stage"><Loader2 className="w-3 h-3 animate-spin" />{aiStage || "starting"} — usually one to two minutes, you can keep this open</div>}
        <p className="text-[11px] leading-relaxed text-slate-700">
          Reads the ticked documents (scans are OCR'd), the Legal calendar, the timeline and the logged e-mails, then proposes the order type, the court and case number, the guided fields,
          the Q3 / Q10 wording in our house style, a draft order and the enclosures. <b>{ot?.label}</b> is pre-selected — the AI may suggest a different type; you decide. Everything is editable on the next step.
        </p>
        <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} disabled={aiBusy} rows={2} className="text-xs bg-white" data-testid="n244-ai-instructions"
          placeholder="Optional — anything the AI should know, e.g. 'the hearing was dismissed because our certificate of service was not on the court file' · 'serve by e-mail as well as post'" />
        {aiResult && (
          <div className="rounded border border-amber-200 bg-white p-2 text-[11px] text-slate-800 space-y-1" data-testid="n244-ai-result">
            <div className="font-medium">AI proposal applied — {ctx.order_types.find((o) => o.key === aiResult.order_type)?.label}{aiResult.confidence != null ? ` · confidence ${Math.round(aiResult.confidence * 100)}%` : ""}</div>
            {aiResult.read?.map((r, i) => <div key={i} className={cn("text-[10px]", r.ok ? "text-slate-600" : "text-rose-700")}>{r.ok ? `✓ ${r.name} — ${r.read === "ocr" ? "scanned, OCR'd" : "read"} (${r.chars.toLocaleString()} chars)` : `✗ ${r.name} — ${r.error || "no text"}`}</div>)}
            {aiResult.notes?.map((n, i) => <div key={i} className="text-amber-900">• {n}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}
